import { BASE_URL } from '../../../settings';

// Agent endpoints live inside the main backend (searchservice).
// Default route: `${BASE_URL}search/agent` → `/api/search/agent/...`
// via the API gateway. Override with REACT_APP_AGENT_URL to bypass the
// gateway during local development.
const AGENT_URL = process.env.REACT_APP_AGENT_URL || `${BASE_URL}search/agent`;

/**
 * Sends a message to the agent backend and returns the reply,
 * any triggered action, and the updated conversation history.
 *
 * @param {string} message           - User's message
 * @param {Array}  history           - Conversation history [{role, content}]
 * @param {object} userContext       - { userName, isLoggedIn, bookingCount }
 * @returns {Promise<{ reply: string, action: object|null, history: Array }>}
 */
export const sendAgentMessage = async (message, history = [], userContext = {}) => {
    const response = await fetch(`${AGENT_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, history, userContext }),
    });

    if (!response.ok) {
        throw new Error(`Agent API error: ${response.status}`);
    }

    return response.json();
};

const MAX_CARDS_PER_SEARCH = 30;

/**
 * Fetches the next page of spaces for an existing search (no LLM).
 *
 * @param {object} searchContext - Criteria from the original search
 * @param {number} offset - API offset from pagination.nextOffset (not card count)
 */
export const loadMoreSearchSpaces = async (searchContext, offset) => {
    const response = await fetch(`${AGENT_URL}/search-more`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ searchContext, offset }),
    });

    if (!response.ok) {
        throw new Error(`Search-more API error: ${response.status}`);
    }

    return response.json();
};

export const synthesizeAgentSpeech = async (text, voice) => {
    const response = await fetch(`${AGENT_URL}/voice/speech`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice }),
    });

    if (!response.ok) {
        throw new Error(`Agent voice API error: ${response.status}`);
    }

    return response.blob();
};

export const REALTIME_VOICE = 'coral';

/**
 * Mints a short-lived OpenAI Realtime session for live voice mode.
 * The backend returns an ephemeral client secret used to open a WebRTC
 * session directly with OpenAI (the real API key never reaches the browser).
 *
 * @param {object} userContext - { userName, isLoggedIn, bookingCount }
 * @param {string} [voice]     - Optional voice id override (e.g. "coral")
 * @returns {Promise<object>}  - OpenAI session object incl. client_secret
 */
export const createRealtimeSession = async (userContext = {}, voice) => {
    const response = await fetch(`${AGENT_URL}/voice/realtime-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userContext, voice }),
    });

    if (!response.ok) {
        throw new Error(`Realtime session API error: ${response.status}`);
    }

    return response.json();
};

/**
 * Executes a single agent tool call from the live voice session (e.g. the
 * model calling search_available_spaces) against the backend, reusing the same
 * search/booking logic as text chat.
 *
 * @param {string} name        - Tool name
 * @param {object} args        - Tool arguments from the model
 * @param {object} userContext - { userName, isLoggedIn, bookingCount }
 * @param {object} [extra]     - Optional { message, history, spaces }
 * @returns {Promise<{modelOutput: object, spaces?: Array, filters?: object,
 *   pagination?: object, searchContext?: object, searchMeta?: object, action?: object}>}
 */
export const execAgentTool = async (name, args, userContext = {}, extra = {}) => {
    const response = await fetch(`${AGENT_URL}/tool-exec`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, arguments: args, userContext, ...extra }),
    });

    if (!response.ok) {
        throw new Error(`Tool exec API error: ${response.status}`);
    }

    return response.json();
};

export { MAX_CARDS_PER_SEARCH };
